import { useState } from "react";
import api, { formatError } from "../../utils/api";
import { exportDiagnosticPDF } from "../../utils/exportPDF";
import { Play, RefreshCw, AlertTriangle, CheckCircle, TrendingUp, Users, Flag, Map, FileDown, Share2, Copy, X } from "lucide-react";

const SEVERITY_COLORS = { High: "bg-red-50 border-red-300 text-red-800", Medium: "bg-amber-50 border-amber-300 text-amber-800", Low: "bg-blue-50 border-blue-300 text-blue-800" };
const PRIORITY_BADGE = { Critical: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700", Medium: "bg-yellow-100 text-yellow-700", Low: "bg-sage-100 text-sage-600" };

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md mb-4">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-sage-100 bg-sage-50">
        <Icon className="w-4 h-4 text-sage-500" />
        <h3 className="font-heading text-sm font-semibold text-sage-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function DiagnosticTab({ project, company, branding, updateDiagnostic }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const diag = project?.diagnostic;
  const org = project?.setup?.org_profile || {};
  const vc = project?.setup?.volume_capacity || {};

  const runDiagnostic = async () => {
    setRunning(true);
    setError("");
    try {
      const res = await api.post(`/projects/${project.id}/diagnostic`);
      updateDiagnostic(res.data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setRunning(false);
    }
  };

  const handleExportPDF = () => {
    if (!diag) return;
    exportDiagnosticPDF({ project, company, diagnostic: diag, branding });
  };

  const handleShare = async () => {
    if (!diag) return;
    setShareBusy(true);
    setError("");
    try {
      const res = await api.post(`/projects/${project.id}/share`, { expires_days: 30 });
      const base = window.location.origin;
      setShareInfo({ url: `${base}/share/${res.data.token}`, expires_at: res.data.expires_at });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setShareBusy(false);
    }
  };

  const handleCopy = () => {
    if (!shareInfo) return;
    navigator.clipboard.writeText(shareInfo.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    try {
      await api.delete(`/projects/${project.id}/share`);
      setShareInfo(null);
    } catch (err) {
      setError(formatError(err));
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 8</p>
          <h2 className="font-heading text-2xl font-semibold text-sage-900">Lean Diagnostic</h2>
          <p className="text-sage-500 text-sm mt-1">On-demand diagnostic report with actionable insights based on your current model data</p>
        </div>
        <div className="flex items-center gap-2">
          {diag && (
            <button
              data-testid="share-diagnostic-button"
              onClick={handleShare}
              disabled={shareBusy}
              className="flex items-center gap-2 border border-sage-200 text-sage-700 rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-sage-50 transition-colors disabled:opacity-60"
            >
              <Share2 className="w-4 h-4" /> {shareBusy ? "Creating..." : "Share Link"}
            </button>
          )}
          {diag && (
            <button
              data-testid="export-diagnostic-pdf-button"
              onClick={handleExportPDF}
              className="flex items-center gap-2 border border-sage-200 text-sage-700 rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-sage-50 transition-colors"
            >
              <FileDown className="w-4 h-4" /> Export PDF
            </button>
          )}
          <button
            data-testid="run-diagnostic-button"
            onClick={runDiagnostic}
            disabled={running || !project?.stages?.length}
            className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-sage-700 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:transform-none"
          >
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {running ? "Running..." : diag ? "Refresh Diagnostic" : "Run Diagnostic"}
          </button>
        </div>
      </div>

      {shareInfo && (
        <div className="bg-sage-50 border border-sage-200 rounded-md p-4 mb-4 flex items-start justify-between gap-3" data-testid="share-link-box">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1">Read-only share link · Expires {new Date(shareInfo.expires_at).toLocaleDateString()}</p>
            <code className="text-xs bg-white border border-sage-200 rounded px-2 py-1 block truncate select-all text-sage-800" data-testid="share-link-url">{shareInfo.url}</code>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button data-testid="copy-share-link" onClick={handleCopy} className="border border-sage-300 text-sage-700 rounded-md px-2 py-1 text-xs hover:bg-white flex items-center gap-1">
              <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy"}
            </button>
            <button data-testid="revoke-share-link" onClick={handleRevoke} className="border border-red-200 text-red-600 rounded-md px-2 py-1 text-xs hover:bg-red-50 flex items-center gap-1">
              <X className="w-3 h-3" /> Revoke
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {!diag && !running && (
        <div className="text-center py-16 bg-white border border-dashed border-sage-200 rounded-md">
          <Flag className="w-10 h-10 text-sage-300 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-medium text-sage-700 mb-2">Ready to generate your Lean Diagnostic</h3>
          <p className="text-sage-400 text-sm max-w-md mx-auto">
            {!project?.stages?.length ? "Configure process stages first (Tab 2), then return here to run the diagnostic." : "Click \"Run Diagnostic\" above to analyse your workforce model and generate actionable recommendations."}
          </p>
        </div>
      )}

      {diag && (
        <>
          {/* Report Header */}
          <Section icon={Flag} title="Report Overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Organisation", value: diag.org_name || org.company_name || "—" },
                { label: "Domain", value: diag.domain || "—" },
                { label: "Stages Analysed", value: diag.stage_count || 0 },
                { label: "Monthly Volume", value: (diag.total_volume || 0).toLocaleString() },
                { label: "Total FTE", value: diag.summary_stats?.total_current_fte || 0 },
                { label: "Required FTE", value: diag.summary_stats?.total_required_fte || 0 },
                { label: "Headcount Delta", value: `${(diag.summary_stats?.headcount_delta || 0) > 0 ? "+" : ""}${diag.summary_stats?.headcount_delta || 0}` },
                { label: "Potential Saving/Mo", value: `${org.currency || vc.currency || "USD"} ${((diag.summary_stats?.potential_saving || 0) / 1000).toFixed(0)}K` },
              ].map(({ label, value }) => (
                <div key={label} className="bg-sage-50 rounded-md p-3">
                  <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
                  <p className="font-heading text-base font-bold text-sage-900">{value}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-sage-400 mt-3">Generated: {diag.generated_at ? new Date(diag.generated_at).toLocaleString() : "—"}</p>
          </Section>

          {/* Workforce Composition */}
          {diag.workforce_intel && (
            <Section icon={Users} title="Workforce Composition Intelligence">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {[
                  { label: "Local Nationals", value: `${diag.workforce_intel.local_count} (${diag.workforce_intel.local_pct}%)` },
                  { label: "Expatriates", value: `${diag.workforce_intel.expat_count} (${(100 - diag.workforce_intel.local_pct).toFixed(1)}%)` },
                  { label: "Local Hrs/Month", value: `${diag.workforce_intel.monthly_hrs_local}` },
                  { label: "Expat Hrs/Month", value: `${diag.workforce_intel.monthly_hrs_expat}` },
                  { label: "Conversion Factor", value: `1 local = ${diag.workforce_intel.conversion_factor}× expat` },
                  { label: "Localisation Status", value: diag.workforce_intel.localization_status?.replace("_", " ") || "—", color: diag.workforce_intel.localization_status === "on_target" ? "text-green-600" : diag.workforce_intel.localization_status === "above" ? "text-blue-600" : "text-red-600" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-sage-50 rounded-md p-3">
                    <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
                    <p className={`font-heading text-sm font-bold capitalize ${color || "text-sage-900"}`}>{value}</p>
                  </div>
                ))}
              </div>
              {diag.workforce_intel.emiratisation_alert && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                  <strong>Emiratisation Planning Alert:</strong> Local national mix exceeds 30%, which triggers additional capacity planning considerations. Local FTE hours are significantly lower than expatriate hours — ensure your FTE model accounts for this capacity differential when planning roster coverage and growth.
                </div>
              )}
            </Section>
          )}

          {/* Priority Actions */}
          {diag.priority_actions?.length > 0 && (
            <Section icon={TrendingUp} title="Priority Focus Areas — Top 3 Stages">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {diag.priority_actions.map((stage, i) => (
                  <div key={i} className="border border-sage-200 rounded-md p-4" data-testid={`priority-stage-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 bg-sage-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">#{i + 1}</span>
                      <h4 className="font-heading text-sm font-semibold text-sage-900">{stage.name}</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-sage-400">Req FTE: </span><span className="font-semibold text-sage-800">{stage.required_fte}</span></div>
                      <div><span className="text-sage-400">Current: </span><span className="font-semibold text-sage-800">{stage.current_fte}</span></div>
                      <div><span className="text-sage-400">Yield %: </span><span className="font-semibold text-sage-800">{stage.yield_pct}%</span></div>
                      <div><span className="text-sage-400">AHT: </span><span className="font-semibold text-sage-800">{stage.aht} min</span></div>
                    </div>
                    <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_BADGE[stage.priority] || PRIORITY_BADGE.Medium}`}>{stage.priority}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Waste Alerts */}
          {diag.alerts?.length > 0 && (
            <Section icon={AlertTriangle} title="Waste Diagnostic Alerts">
              <div className="space-y-3">
                {diag.alerts.map((alert, i) => (
                  <div key={i} className={`rounded-md p-4 border ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.Medium}`} data-testid={`diagnostic-alert-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-sm">{alert.type}: {alert.value}%</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${alert.severity === "High" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{alert.severity}</span>
                    </div>
                    <p className="text-xs leading-relaxed opacity-90">{alert.message}</p>
                    {alert.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {alert.tags.map((tag) => (
                          <span key={tag} className="text-[10px] bg-white/60 border border-current/20 rounded px-1.5 py-0.5 font-medium">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Lean Levers */}
          {diag.lean_levers?.length > 0 && (
            <Section icon={TrendingUp} title={`Domain Lean Levers — ${diag.domain}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {diag.lean_levers.map((lever, i) => (
                  <div key={i} className="border border-sage-200 rounded-md p-4" data-testid={`lean-lever-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-heading text-sm font-semibold text-sage-900 leading-snug">{lever.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-2 flex-shrink-0 ${PRIORITY_BADGE[lever.priority] || PRIORITY_BADGE.Medium}`}>{lever.priority}</span>
                    </div>
                    <p className="text-xs text-sage-600 leading-relaxed">{lever.body}</p>
                    {lever.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {lever.tags.map((tag) => (
                          <span key={tag} className="text-[10px] bg-sage-50 border border-sage-200 text-sage-600 rounded px-1.5 py-0.5">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Lean Roadmap */}
          {diag.roadmap?.length > 0 && (
            <Section icon={Map} title="Lean Programme Roadmap">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {diag.roadmap.map((phase) => (
                  <div key={phase.phase} className="relative" data-testid={`roadmap-phase-${phase.phase}`}>
                    <div className="border border-[#E2E8E3] rounded-md p-4 h-full">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 bg-sage-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{phase.phase}</span>
                        <span className="text-xs bg-sage-100 text-sage-600 px-2 py-0.5 rounded-full font-medium">{phase.months} mo</span>
                      </div>
                      <h4 className="font-heading text-sm font-semibold text-sage-900 mb-1">{phase.title}</h4>
                      <p className="text-xs text-sage-500 mb-3 leading-relaxed">{phase.description}</p>
                      <ul className="space-y-1">
                        {phase.actions?.map((action, ai) => (
                          <li key={ai} className="flex items-start gap-1.5 text-xs text-sage-600">
                            <CheckCircle className="w-3 h-3 mt-0.5 text-sage-400 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
